import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CalendarDaysIcon,
  ChartBarIcon,
  EllipsisVerticalIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ExclamationCircleIcon,
  PencilIcon,
  TrashIcon,
  KeyIcon
} from '@heroicons/react/24/outline';
import {
  getCompanyProjectsWithConfig,
  getSharedProjectsWithConfig,
  redeemProjectShareKey,
  updateProject
} from '../utils/supabaseClient';
import { useUser } from '../context/UserContext';

import ProjectFolderIcon from '../components/ProjectFolderIcon';

export default function Projects({ currentCompany }) {
  const navigate = useNavigate();
  const { currentCompany: contextCompany } = useUser();
  const activeCompany = currentCompany || contextCompany;
  const [projects, setProjects] = useState([]);
  const [activeTab, setActiveTab] = useState('owned');
  const [initialLoading, setInitialLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [shareKey, setShareKey] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [redeemError, setRedeemError] = useState('');
  const [redeemMessage, setRedeemMessage] = useState('');
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renamingProject, setRenamingProject] = useState(null);
  const [renameLoading, setRenameLoading] = useState(false);
  const menuRef = useRef(null);
  const isDotOrg = activeCompany?.org_type === 'dot';

  useEffect(() => {
    loadProjects();
  }, [activeTab, isDotOrg]);

  useEffect(() => {
    if (!isDotOrg && activeTab === 'shared') {
      setActiveTab('owned');
    }
  }, [activeTab, isDotOrg]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadProjects = async () => {
    try {
      setListLoading(true);
      setError('');
      
      const projectsData = isDotOrg && activeTab === 'shared'
        ? await getSharedProjectsWithConfig()
        : await getCompanyProjectsWithConfig();
      
      const processedProjects = (projectsData || []).map(project => {
        let sessionCount = 0;
        try {
          if (Array.isArray(project.prototype_sessions) && project.prototype_sessions.length > 0) {
            sessionCount = project.prototype_sessions[0]?.count || 0;
          }
        } catch (sessionError) {
          console.error('Error processing sessions for project:', project.id, sessionError);
          sessionCount = 0;
        }

        return {
          ...project,
          sessionInfo: {
            count: project.session_count !== undefined ? Number(project.session_count || 0) : sessionCount,
            mostRecent: null,
            lastActivity: project.created_at
          }
        };
      });

      setProjects(processedProjects);
      
    } catch (err) {
      console.error('Error loading projects:', err);
      setError('Failed to load projects. Please try again.');
    } finally {
      setListLoading(false);
      setInitialLoading(false);
    }
  };

  const filteredProjects = projects.filter(project =>
    project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (project.route_lrs_key && project.route_lrs_key.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleProjectClick = (projectId) => {
    navigate(`/projects/${projectId}`);
  };

  const normalizeShareKey = (value) => {
    return value.replace(/[\s-]/g, '').toUpperCase().slice(0, 7);
  };

  const handleRedeemShareKey = async (event) => {
    event.preventDefault();
    const normalized = normalizeShareKey(shareKey);
    if (normalized.length !== 7) {
      setRedeemError('Enter the 7-character DOT share key.');
      return;
    }

    try {
      setRedeeming(true);
      setRedeemError('');
      setRedeemMessage('');
      const result = await redeemProjectShareKey(normalized);
      setShareKey('');
      setShowRedeemModal(false);
      setRedeemMessage('Project shared with your organization.');
      if (activeTab === 'shared') {
        await loadProjects();
      } else {
        setActiveTab('shared');
      }
      if (result?.project_id) {
        navigate(`/projects/${result.project_id}`);
      }
    } catch (err) {
      setRedeemError(err.message || 'Failed to redeem project share key');
    } finally {
      setRedeeming(false);
    }
  };

  const handleCloseRedeemModal = () => {
    if (redeeming) return;
    setShowRedeemModal(false);
    setRedeemError('');
    setShareKey('');
  };

  const handleMenuClick = (e, projectId) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === projectId ? null : projectId);
  };

  const handleRenameProject = async (newName) => {
    if (!renamingProject || !newName.trim()) {
      setError('Project name is required');
      return;
    }

    setRenameLoading(true);
    setError('');

    try {
      await updateProject(renamingProject.id, {
        name: newName.trim()
      });

      setProjects(prev => prev.map(p => 
        p.id === renamingProject.id 
          ? { ...p, name: newName.trim() }
          : p
      ));

      setShowRenameModal(false);
      setRenamingProject(null);
      setOpenMenuId(null);
    } catch (err) {
      console.error('Error renaming project:', err);
      setError(err.message || 'Failed to rename project');
    } finally {
      setRenameLoading(false);
    }
  };

  const handleRenameClick = (project) => {
    setRenamingProject(project);
    setShowRenameModal(true);
    setOpenMenuId(null);
    setError('');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getRelativeDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
    if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} months ago`;
    return `${Math.floor(diffInDays / 365)} years ago`;
  };

  const ProjectActionMenu = ({ project, isOpen, onClose }) => {
    if (!isOpen) return null;

    return (
      <div
        ref={menuRef}
        className="absolute right-0 top-8 w-48 bg-white rounded-sm shadow-lg border border-gray-200 py-1 z-50"
      >
        <button
          onClick={() => handleRenameClick(project)}
          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
        >
          <PencilIcon className="h-4 w-4 mr-3 text-gray-400" />
          Rename Project
        </button>
        <button
          onClick={() => {
            onClose();
          }}
          className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center"
        >
          <TrashIcon className="h-4 w-4 mr-3 text-red-400" />
          Delete Project
        </button>
      </div>
    );
  };

  if (initialLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header - aligned with Layout sidebar h-14 */}
      <div className="flex-shrink-0 min-h-14 bg-white border-b border-gray-200 px-6 py-2 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4 min-w-0">
          <h1 className="text-lg font-semibold text-gray-900">Projects</h1>
          {isDotOrg && (
            <div className="inline-flex rounded-sm border border-gray-200 overflow-hidden flex-shrink-0">
              <button
                type="button"
                onClick={() => setActiveTab('owned')}
                className={`px-3 py-1.5 text-sm font-medium ${activeTab === 'owned' ? 'bg-red-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              >
                My Projects
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('shared')}
                className={`px-3 py-1.5 text-sm font-medium border-l border-gray-200 ${activeTab === 'shared' ? 'bg-red-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              >
                Shared with Me
              </button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="relative w-64">
            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-3 py-1.5 w-full text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
          </div>
          {isDotOrg && (
            <button
              type="button"
              onClick={() => {
                setRedeemError('');
                setShowRedeemModal(true);
              }}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-sm hover:bg-red-700"
            >
              <KeyIcon className="h-4 w-4 mr-1.5" />
              Redeem Key
            </button>
          )}
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-auto flex flex-col">
        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border-b border-red-200 px-6 py-3">
            <div className="flex items-center">
              <ExclamationCircleIcon className="h-4 w-4 text-red-400 mr-2" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {redeemMessage && (
          <div className="bg-green-50 border-b border-green-200 px-6 py-3">
            <p className="text-sm text-green-700">{redeemMessage}</p>
          </div>
        )}

        {/* Projects Table */}
        <div className="relative flex-1 bg-white">
          {listLoading && !initialLoading && (
            <div className="absolute inset-x-0 top-0 z-10 h-0.5 bg-red-100 overflow-hidden">
              <div className="h-full w-1/3 bg-red-600 animate-pulse" />
            </div>
          )}

          {listLoading && projects.length === 0 && (
            <div className="bg-white h-56 flex items-center justify-center border-b border-gray-200">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <div className="h-4 w-4 rounded-full border-2 border-red-200 border-t-red-600 animate-spin" />
                Loading projects...
              </div>
            </div>
          )}

          {projects.length > 0 && (
            <div className={`bg-white border-b border-gray-200 transition-opacity ${listLoading ? 'opacity-60' : 'opacity-100'}`}>
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sessions</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Activity</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredProjects.map((project) => (
                  <tr 
                    key={project.id}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => handleProjectClick(project.id)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <ProjectFolderIcon className="h-10 w-10 mr-3 flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">{project.name || 'Unnamed Project'}</div>
                          {project.route_lrs_key && <div className="text-sm text-gray-500 truncate">{project.route_lrs_key}</div>}
                          {project.spec_json?.description && <div className="text-xs text-gray-400 truncate max-w-xs mt-1">{project.spec_json.description}</div>}
                          {project.is_shared_project && (
                            <div className="text-xs text-gray-500 truncate max-w-xs mt-1">
                              Shared by {project.owner_org_name || 'owner'} · {project.share_role}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center"><ChartBarIcon className="h-4 w-4 text-gray-400 mr-2" />{project.sessionInfo.count}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center"><CalendarDaysIcon className="h-4 w-4 text-gray-400 mr-2" />{formatDate(project.created_at)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {project.sessionInfo.mostRecent ? getRelativeDate(project.sessionInfo.lastActivity) : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {!project.is_shared_project && (
                        <button onClick={(e) => handleMenuClick(e, project.id)} className="text-gray-400 hover:text-gray-600 p-1 relative">
                          <EllipsisVerticalIcon className="h-4 w-4" />
                          <ProjectActionMenu project={project} isOpen={openMenuId === project.id} onClose={() => setOpenMenuId(null)} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}

          {/* Empty States */}
          {!listLoading && projects.length === 0 && (
            <div className="bg-white h-full flex items-center justify-center">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-100 rounded-full mb-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {activeTab === 'shared' ? 'No Shared Projects Yet' : 'No Projects Yet'}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {activeTab === 'shared'
                  ? 'Projects shared by contractors will appear here after you redeem a DOT share key.'
                  : 'Get started by uploading your first road data file.'}
              </p>
              {activeTab === 'owned' && (
                <button
                  onClick={() => navigate('/upload')}
                  className="mt-4 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-sm hover:bg-red-700"
                >
                  Go to Upload
                </button>
              )}
            </div>
          </div>
          )}

          {!listLoading && projects.length > 0 && filteredProjects.length === 0 && (
            <div className="bg-white h-full flex items-center justify-center">
            <div className="text-center">
              <MagnifyingGlassIcon className="mx-auto h-10 w-10 text-gray-400" />
              <h3 className="mt-3 text-lg font-semibold text-gray-900">No Projects Found</h3>
              <p className="mt-1 text-sm text-gray-500">
                No results for "{searchTerm}"
              </p>
              <button
                onClick={() => setSearchTerm('')}
                className="mt-4 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm hover:bg-gray-50"
              >
                Clear Search
              </button>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Redeem DOT Share Key Modal */}
      {showRedeemModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Redeem Project Key</h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  Enter the 7-character key provided by the contractor.
                </p>
              </div>
              <button
                type="button"
                onClick={handleCloseRedeemModal}
                disabled={redeeming}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleRedeemShareKey} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Share Key
                </label>
                <input
                  type="text"
                  value={shareKey}
                  onChange={(event) => {
                    setShareKey(normalizeShareKey(event.target.value));
                    setRedeemError('');
                  }}
                  placeholder="7KQ9M2P"
                  className="w-full px-3 py-2 border border-gray-300 rounded-sm font-mono text-lg tracking-[0.25em] uppercase focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  autoFocus
                />
              </div>

              {redeemError && (
                <div className="bg-red-50 border border-red-200 rounded-sm p-3">
                  <div className="flex">
                    <ExclamationCircleIcon className="h-4 w-4 text-red-400 mr-2 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-red-700">{redeemError}</p>
                  </div>
                </div>
              )}

              <div className="flex space-x-3 pt-2">
                <button
                  type="button"
                  onClick={handleCloseRedeemModal}
                  disabled={redeeming}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-sm hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={redeeming || shareKey.length !== 7}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-sm hover:bg-red-700 disabled:opacity-50"
                >
                  {redeeming ? 'Redeeming...' : 'Redeem'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Rename Project Modal */}
      {showRenameModal && renamingProject && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Rename Project</h3>
              <button
                onClick={() => {
                  setShowRenameModal(false);
                  setRenamingProject(null);
                  setError('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                const newName = formData.get('projectName');
                handleRenameProject(newName);
              }}
              className="p-6 space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Project Name
                </label>
                <input
                  type="text"
                  name="projectName"
                  defaultValue={renamingProject.name}
                  placeholder="Enter project name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  autoFocus
                  required
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-sm p-3">
                  <div className="flex">
                    <ExclamationCircleIcon className="h-4 w-4 text-red-400 mr-2" />
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                </div>
              )}

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowRenameModal(false);
                    setRenamingProject(null);
                    setError('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-sm hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={renameLoading}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-sm hover:bg-red-700 disabled:opacity-50"
                >
                  {renameLoading ? 'Renaming...' : 'Rename'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
